import { ORDER_STATUS_VALUES } from '@cult/domain';
import { z } from 'zod';
import { OffsetQuerySchema, idField } from './common';

export const OrderStatusSchema = z.enum(ORDER_STATUS_VALUES);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;

export const OrderTransitionInputSchema = z
  .object({
    to: OrderStatusSchema,
    note: z.string().max(2000).optional(),
  })
  .superRefine((d, ctx) => {
    if (d.to === 'rejected' && !d.note?.trim()) {
      ctx.addIssue({ code: 'custom', path: ['note'], message: 'Rejection reason is required' });
    }
  });
export type OrderTransitionInput = z.infer<typeof OrderTransitionInputSchema>;

export const OrderUserEmbedSchema = z.object({
  id: idField,
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
});
export type OrderUserEmbed = z.infer<typeof OrderUserEmbedSchema>;

export const OrderStatusLogEntrySchema = z.object({
  id: idField,
  fromStatus: OrderStatusSchema.nullable(),
  toStatus: OrderStatusSchema,
  changedBy: OrderUserEmbedSchema,
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type OrderStatusLogEntry = z.infer<typeof OrderStatusLogEntrySchema>;

export const OrderStatusLogResponseSchema = z.object({
  entries: z.array(OrderStatusLogEntrySchema),
});
export type OrderStatusLogResponse = z.infer<typeof OrderStatusLogResponseSchema>;

export const OrderItemSchema = z.object({
  skuId: idField,
  title: z.string(),
  imageUrl: z.string().nullable(),
  quantity: z.number().int(),
  unitPrice: z.number().int(),
  lineTotal: z.number().int(),
});
export type OrderItem = z.infer<typeof OrderItemSchema>;

// `orderId` is the order header id.
export const OrderSummarySchema = z.object({
  orderId: idField,
  status: OrderStatusSchema,
  total: z.number().int(),
  // Note from the rejection log entry; null unless the order was rejected.
  rejectionReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  items: z.array(OrderItemSchema),
});
export type OrderSummary = z.infer<typeof OrderSummarySchema>;

export const OrderListResponseSchema = z.array(OrderSummarySchema);
export type OrderListResponse = z.infer<typeof OrderListResponseSchema>;

export const OrderDetailSchema = OrderSummarySchema.extend({
  userId: idField,
  statusLog: z.array(OrderStatusLogEntrySchema),
});
export type OrderDetail = z.infer<typeof OrderDetailSchema>;

// One row per order header (not per item).
export const AdminOrderQuerySchema = OffsetQuerySchema.extend({
  userId: idField.optional(),
  skuId: idField.optional(),
  status: OrderStatusSchema.optional(),
});
export type AdminOrderQuery = z.infer<typeof AdminOrderQuerySchema>;

export const AdminOrderRowSchema = z.object({
  id: idField,
  userId: idField,
  userName: z.string(),
  status: OrderStatusSchema,
  totalPrice: z.number().int(),
  itemCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminOrderRow = z.infer<typeof AdminOrderRowSchema>;

export const AdminOrderListResponseSchema = z.object({
  rows: z.array(AdminOrderRowSchema),
  total: z.number().int(),
});
export type AdminOrderListResponse = z.infer<typeof AdminOrderListResponseSchema>;
